import { map } from 'underscore';
import React, { useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DropdownTreeSelect } from '@bingads-webui-campaign-react/dropdown-tree-select';
import { ActionButton, IconButton } from '@fluentui/react';
import { Tooltip } from '@fluentui/react-components';
import { formatReachEstimation } from '@bingads-webui-campaign-react/ima-reach-representation';

import { getAudienceValue } from '../utils/audience-signal';
import { SectionShimmer } from './shimmer-element';
import { AUDIENCE_MAP } from '../utils/audience-name-mapping';

const getAudienceI18nName = (i18n, audienceName) => {
  const key = getAudienceValue(audienceName);
  return AUDIENCE_MAP[key] ? i18n.getString(AUDIENCE_MAP[key]) : key;
};

export const AudienceInterests = React.memo(({
  isLoading,
  data,
  onDataChange,
  classes,
  customHeadline,
}) => {
  const i18n = useContext(I18nContext);

  const { audienceListInFlatTree, interests = [], valueToFlatItem = {} } = data || {};

  const [openItems, setOpenItems] = useState([]);

  const onChangeInterests = useCallback((newInterests) => {
    onDataChange({ interests: newInterests });
  }, [onDataChange]);

  const onRemoveInterests = useCallback((index) => {
    const newInterest = [
      ...interests.slice(0, index),
      ...interests.slice(index + 1),
    ];
    onChangeInterests(newInterest);
  }, [interests, onChangeInterests]);

  const onRemoveAllInterests = useCallback(() => {
    onChangeInterests([]);
  }, [onChangeInterests]);

  const onRenderTreeItem = useCallback(item => getAudienceI18nName(i18n, item.value), [i18n]);
  const customTreeItemFilter = useCallback((item, searchQuery) => {
    const audienceI18nName = getAudienceI18nName(i18n, item.value) || '';

    return audienceI18nName.toLowerCase().includes(searchQuery.toLowerCase());
  }, [i18n]);

  const onRenderTooltip = useCallback((item = {}) => {
    const {
      value,
      parentValue,
      reachEstimation,
      itemType,
      'aria-level': ariaLevel,
    } = item;

    const isRootLeaf = (ariaLevel === 1 && itemType === 'leaf') || (parentValue === undefined && value);

    return (
      <div className={classes.tooltip}>
        <div className="tooltip-header">
          {!isRootLeaf && <div className="interest-category">{getAudienceI18nName(i18n, parentValue)}</div>}
          <div className="interest-name">{getAudienceI18nName(i18n, value)}</div>
        </div>
        <div className="interest-reach">
          <span>{i18n.getString(_TL_('List size: '))}</span>
          {formatReachEstimation(reachEstimation, i18n)}
        </div>
      </div>
    );
  }, [classes, i18n]);

  if (isLoading) {
    return <SectionShimmer />;
  }

  return (
    <div className="audience-signal-interests">
      <div className={classes.interestsHeader}>
        <div>{customHeadline || i18n.getString(_TL_('Add interests'))}</div>
        <ActionButton iconProps={{ iconName: 'Delete' }} disabled={!interests.length} onClick={onRemoveAllInterests}>
          {i18n.getString(_TL_('Remove all'))}
        </ActionButton>
      </div>
      {
        interests.length > 0 && (
          <div className="audience-dimensions">
            {
              map(interests, (name, index) => (
                <Tooltip
                  key={name}
                  withArrow
                  positioning="after"
                  content={onRenderTooltip(valueToFlatItem[name])}
                  relationship="description"
                >
                  <div className="audience-dimension" key={`audience-${name}`}>
                    <span>{getAudienceI18nName(i18n, name)}</span>
                    <IconButton
                      aria-label={i18n.getString(_TL_('Remove {{name}}'), { name })}
                      iconProps={{ iconName: 'Cancel' }}
                      onClick={() => onRemoveInterests(index)}
                    />
                  </div>
                </Tooltip>
              ))
            }
          </div>
        )
      }
      <DropdownTreeSelect
        items={audienceListInFlatTree}
        checkedItems={interests}
        onChange={onChangeInterests}
        openItems={openItems}
        onOpenChange={setOpenItems}
        placeholder={i18n.getString(_TL_('Search or browse interests'))}
        onRenderTooltip={onRenderTooltip}
        onRenderContent={onRenderTreeItem}
        customFilter={customTreeItemFilter}
      />
    </div>
  );
});

AudienceInterests.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  customHeadline: PropTypes.string,
  classes: PropTypes.shape({
    interestsHeader: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
};

AudienceInterests.defaultProps = {
  customHeadline: null,
};
